/**
 * VerdictPanel — PreservationWitness validator verdict surface.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { severityColor } from "../../severityColor.js";
import type { Verdict } from "../../algorithms/validatePreservation.js";

export function VerdictPanel({
  verdict,
}: {
  readonly verdict: Verdict;
}): React.JSX.Element {
  if (verdict.outcome === "accept") {
    return <AcceptedVerdict subject={verdict.subject} />;
  }
  return <RefusedVerdict verdict={verdict} />;
}

function AcceptedVerdict({
  subject,
}: {
  readonly subject: string;
}): React.JSX.Element {
  return (
    <PanelSurface.Card
      glowColor="rgba(34,197,94,0.18)"
      style={{ borderColor: `${panelTokens.accentGreen}66` }}
    >
      <div className="flex items-baseline gap-3">
        <VerdictLabel color={panelTokens.accentGreen}>ACCEPT</VerdictLabel>
        <span className="text-[var(--ilg-text)] text-[13px]">
          GovArrow constructed for <PanelSurface.InlineCode>{subject}</PanelSurface.InlineCode>.
        </span>
      </div>
      <p className="m-0 mt-3 text-[13px] text-[var(--ilg-muted)] leading-[1.7]">
        The witness types under the IssuerCapability scope, all structural
        checks pass, and the preservation lift is admitted into the governed
        cone <PanelSurface.InlineCode>𝓔_I</PanelSurface.InlineCode>.
      </p>
    </PanelSurface.Card>
  );
}

function RefusedVerdict({
  verdict,
}: {
  readonly verdict: Extract<Verdict, { outcome: "refuse" }>;
}): React.JSX.Element {
  const color = severityColor(verdict.severity);
  const badges = [
    { id: `${verdict.rule}-rule`, label: verdict.rule, color },
    {
      id: `${verdict.rule}-severity`,
      label: `severity: ${verdict.severity}`,
      color: panelTokens.muted,
    },
  ];

  return (
    <PanelSurface.Card glowColor={`${color}28`} style={{ borderColor: `${color}66` }}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <VerdictLabel color={color}>REFUSE</VerdictLabel>
        <PanelSurface.BadgeRail
          ariaLabel={`${verdict.rule} refusal metadata`}
          items={badges}
        />
      </div>
      <p className="m-0 mt-3.5 text-[14px] text-[var(--ilg-bright)] font-semibold leading-[1.6]">
        {verdict.title}
      </p>
      <p className="m-0 mt-2 text-[13px] text-[var(--ilg-text)] leading-[1.7]">
        {verdict.explanation}
      </p>
      <p className="m-0 mt-3.5 text-[12px] text-[var(--ilg-muted)] font-mono">
        subject: {verdict.subject}
      </p>
    </PanelSurface.Card>
  );
}

function VerdictLabel({
  children,
  color,
}: {
  readonly children: React.ReactNode;
  readonly color: string;
}): React.JSX.Element {
  return (
    <span
      className="font-mono font-extrabold text-[18px]"
      style={{
        color,
        textShadow: `0 0 14px ${color}55`,
      }}
    >
      {children}
    </span>
  );
}
